import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthProvider";
import { hasPermission, PERMISSIONS } from "../utils/rbac";
import {
    CalendarIcon,
    CurrencyDollarIcon,
    DocumentTextIcon,
    ShieldCheckIcon,
    ClipboardDocumentListIcon,
    UserGroupIcon,
    ShoppingBagIcon
} from "@heroicons/react/24/outline";

const HomePage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Define cards based on user permissions
    const getCards = () => {
        const allCards = [
            {
                title: "Shore Excursions Bidding",
                description: "Submit pricing and proposals for shore excursion services",
                icon: CurrencyDollarIcon,
                path: "/rfp",
                color: "bg-blue-50 dark:bg-blue-900",
                iconColor: "text-blue-600 dark:text-blue-300",
                permissions: [PERMISSIONS.CREATE_BIDS, PERMISSIONS.VIEW_BIDS],
                requireAll: false,
            },
            {
                title: "Calls Calendar",
                description: "View upcoming cruise calls and schedules",
                icon: CalendarIcon,
                path: "/calendar",
                color: "bg-purple-50 dark:bg-purple-900",
                iconColor: "text-purple-600 dark:text-purple-300",
                permissions: [PERMISSIONS.VIEW_CALENDAR],
            },
            {
                title: "Pending Approvals",
                description: "Review and manage vendor approval requests",
                icon: ShieldCheckIcon,
                path: "/vendor-pending-approval",
                color: "bg-amber-50 dark:bg-amber-900",
                iconColor: "text-amber-600 dark:text-amber-300",
                permissions: [PERMISSIONS.VIEW_PENDING_APPROVALS, PERMISSIONS.APPROVE_VENDORS],
                requireAll: false,
            },
            {
                title: "Service Confirmations",
                description: "Manage and confirm services for upcoming cruises",
                icon: DocumentTextIcon,
                path: "/service-confirmation",
                color: "bg-emerald-50 dark:bg-emerald-900",
                iconColor: "text-emerald-600 dark:text-emerald-300",
                permissions: [PERMISSIONS.VIEW_SERVICE_CONFIRMATIONS],
            },
            {
                title: "RFP Admin Panel",
                description: "Manage and review submitted RFPs and proposals",
                icon: ClipboardDocumentListIcon,
                path: "/rfp-admin",
                color: "bg-rose-50 dark:bg-rose-900",
                iconColor: "text-rose-600 dark:text-rose-300",
                permissions: [PERMISSIONS.VIEW_RFP_ADMIN],
            },
            {
                title: "User Management",
                description: "Manage user accounts, roles, and permissions",
                icon: UserGroupIcon,
                path: "/users",
                color: "bg-indigo-50 dark:bg-indigo-900",
                iconColor: "text-indigo-600 dark:text-indigo-300",
                permissions: [PERMISSIONS.MANAGE_USERS, PERMISSIONS.VIEW_USERS],
                requireAll: false,
            },
            {
                title: "Vendor Management",
                description: "Manage vendor types and their specific permissions",
                icon: ShoppingBagIcon,
                path: "/vendor-management",
                color: "bg-teal-50 dark:bg-teal-900",
                iconColor: "text-teal-600 dark:text-teal-300",
                permissions: [PERMISSIONS.MANAGE_VENDORS, PERMISSIONS.VIEW_VENDORS],
                requireAll: false,
            },
            {
                title: "RBAC Configuration",
                description: "Configure roles, permissions, and access control settings",
                icon: ShieldCheckIcon,
                path: "/rbac-config",
                color: "bg-gray-50 dark:bg-gray-900",
                iconColor: "text-gray-600 dark:text-gray-300",
                permissions: [PERMISSIONS.SYSTEM_CONFIG],
            }
        ];

        if (!user || !user.rules) {
            // Return cards for non-authenticated users
            return allCards.filter(card => !card.permissions);
        }

        // Filter cards based on user permissions
        const userRules = user.rules || [];
        return allCards.filter(card => {
            if (!card.permissions) return true; // Cards without permissions are always shown
            
            // Check if user has any of the required permissions
            return card.permissions.some(permission => 
                hasPermission(userRules, permission, { userRoles: userRules })
            );
        });
    };

    const filteredCards = getCards();

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-12 text-center">
                <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
                    Cruise Saudi External Portal
                </h1>
                {/* <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                    Welcome to the centralized platform for managing tour services, vendor approvals,
                    and bidding opportunities for cruise ships
                </p> */}
            </div>

            {!user && (
                <div className="py-6 px-4 mb-10 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-center">
                    <h2 className="text-xl font-semibold mb-2 text-blue-800 dark:text-blue-300">
                        New to Cruise Saudi?
                    </h2>
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                        Sign in to access vendor bidding, service management and more
                    </p>
                    <div className="flex justify-center gap-4">
                        <button
                            onClick={() => navigate('/signin')}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition"
                        >
                            Sign In
                        </button>
                        <button
                            onClick={() => navigate('/signup')}
                            className="px-6 py-2 border border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 dark:text-blue-300 dark:border-blue-500 rounded-md transition"
                        >
                            Register
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCards.map((card, index) => (
                    <div
                        key={index}
                        onClick={() => navigate(card.path)}
                        className={`${card.color} p-6 rounded-lg shadow-sm hover:shadow-md cursor-pointer transition-all duration-200 border border-gray-100 dark:border-gray-700`}
                    >
                        <div className={`inline-block p-3 rounded-full ${card.iconColor} bg-white dark:bg-gray-800 mb-4`}>
                            <card.icon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                            {card.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                            {card.description}
                        </p>
                    </div>
                ))}
            </div>

            {/* <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 p-6 rounded-lg border border-gray-100 dark:border-gray-700">
                    <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
                        Why Choose Cruise Saudi
                    </h2>
                    <ul className="space-y-3">
                        <li className="flex items-start">
                            <div className="mr-3 mt-1 text-blue-600 dark:text-blue-300">
                                <ShoppingBagIcon className="w-5 h-5" />
                            </div>
                            <span className="text-gray-700 dark:text-gray-300">Access to premium cruise tourism opportunities</span>
                        </li>
                        <li className="flex items-start">
                            <div className="mr-3 mt-1 text-blue-600 dark:text-blue-300">
                                <UserGroupIcon className="w-5 h-5" />
                            </div>
                            <span className="text-gray-700 dark:text-gray-300">Streamlined vendor management process</span>
                        </li>
                        <li className="flex items-start">
                            <div className="mr-3 mt-1 text-blue-600 dark:text-blue-300">
                                <CalendarIcon className="w-5 h-5" />
                            </div>
                            <span className="text-gray-700 dark:text-gray-300">Real-time cruise call scheduling and planning</span>
                        </li>
                    </ul>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 p-6 rounded-lg border border-gray-100 dark:border-gray-700">
                    <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
                        Latest Updates
                    </h2>
                    <div className="space-y-4">
                        <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="font-medium text-gray-900 dark:text-white">Shore Excursion RFPs Open</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Accepting new tour proposals for the upcoming season</p>
                        </div>
                        <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="font-medium text-gray-900 dark:text-white">New Cruise Schedule Available</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Updated call calendar for 2023-2024 season</p>
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">Vendor Registration Open</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Now accepting new service providers</p>
                        </div>
                    </div>
                </div>
            </div> */}
        </div>
    );
};

export default HomePage; 